import { useState, useEffect, useRef } from "react";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const CardOfTheDay = () => {
  const [card, setCard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [flipped, setFlipped] = useState(false);
  const [rotate, setRotate] = useState({ x: 0, y: 0 });
  const [glarePos, setGlarePos] = useState({ x: 50, y: 50 });
  const cardRef = useRef(null);
  const [visible, setVisible] = useState(false);
  const sectionRef = useRef(null);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.2 });
    if (sectionRef.current) obs.observe(sectionRef.current);
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    axios.get(`${API}/card-of-the-day`)
      .then(r => { setCard(r.data.card); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  const handleMouseMove = (e) => {
    const el = cardRef.current;
    if (!el) return;
    const rect = el.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const y = e.clientY - rect.top;
    const cx = rect.width / 2;
    const cy = rect.height / 2;
    setRotate({
      x: ((y - cy) / cy) * -15,
      y: ((x - cx) / cx) * 15,
    });
    setGlarePos({ x: (x / rect.width) * 100, y: (y / rect.height) * 100 });
  };

  const handleMouseLeave = () => setRotate({ x: 0, y: 0 });

  if (!card && !loading) return null;

  const image = card?.card_images?.[0]?.image_url;
  const atk = card?.atk;
  const def = card?.def;
  const level = card?.level;
  const isMonster = card?.type?.toLowerCase().includes('monster') || (!card?.type?.toLowerCase().includes('spell') && !card?.type?.toLowerCase().includes('trap'));
  const frameColors = {
    'fusion': 'from-purple-700 to-purple-900',
    'synchro': 'from-gray-200 to-gray-400',
    'xyz': 'from-gray-900 to-black',
    'link': 'from-blue-700 to-blue-900',
    'ritual': 'from-blue-900 to-indigo-900',
    'spell': 'from-teal-700 to-green-900',
    'trap': 'from-pink-700 to-purple-900',
  };
  const frameKey = Object.keys(frameColors).find(k => card?.frameType?.toLowerCase().includes(k) || card?.type?.toLowerCase().includes(k));
  const cardGradient = frameColors[frameKey] || 'from-amber-700 to-yellow-900';

  const today = new Date().toLocaleDateString('es-ES', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });

  const shareText = `Carta del Día en Nirvana TCG: ${card?.name || ''}. ${atk !== undefined ? `ATK: ${atk} / DEF: ${def}` : ''} - nirvanatcg.store`;

  return (
    <section
      ref={sectionRef}
      className="relative py-16 md:py-24 overflow-hidden"
      style={{ background: 'linear-gradient(180deg,#050508,#08030f,#050508)' }}
      data-testid="card-of-the-day"
    >
      {/* BG glow effect */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right,transparent,rgba(212,175,55,0.4),transparent)' }} />
        <div className="absolute bottom-0 left-0 right-0 h-px" style={{ background: 'linear-gradient(to right,transparent,rgba(212,175,55,0.2),transparent)' }} />
        <div className="absolute top-1/2 left-1/2 w-96 h-96 -translate-x-1/2 -translate-y-1/2 rounded-full opacity-10" style={{ background: 'radial-gradient(circle, #d4af37, transparent)' }} />
      </div>

      <div className="container mx-auto px-6 md:px-12">
        <div className="flex flex-col lg:flex-row items-center gap-12 lg:gap-20">
          {/* Left: 3D Card */}
          <div
            className="relative flex-shrink-0 flex flex-col items-center"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateY(0)' : 'translateY(40px)',
              transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1)',
            }}
          >
            {/* Card wrapper with 3D effect */}
            <div
              ref={cardRef}
              className="relative cursor-pointer"
              style={{
                width: '260px',
                transformStyle: 'preserve-3d',
                transform: flipped
                  ? `perspective(800px) rotateY(180deg)`
                  : `perspective(800px) rotateX(${rotate.x}deg) rotateY(${rotate.y}deg)`,
                transition: flipped ? 'transform 0.6s ease' : 'transform 0.1s ease',
                filter: loading ? 'blur(8px)' : 'none',
              }}
              onMouseMove={handleMouseMove}
              onMouseLeave={handleMouseLeave}
              onClick={() => setFlipped(f => !f)}
            >
              {/* Front face */}
              <div className="relative" style={{ backfaceVisibility: 'hidden' }}>
                {/* Outer border glow */}
                <div
                  className="absolute -inset-2 rounded-2xl opacity-70"
                  style={{
                    background: `conic-gradient(from 0deg, #d4af37, #dc2626, #7c3aed, #d4af37)`,
                    filter: 'blur(8px)',
                    animation: 'spin 4s linear infinite',
                  }}
                />
                {/* Card frame */}
                <div className={`relative rounded-xl overflow-hidden bg-gradient-to-br ${cardGradient} p-0.5`} style={{ width: '260px', boxShadow: '0 20px 60px rgba(0,0,0,0.8)' }}>
                  <div className="rounded-xl overflow-hidden bg-gray-950 aspect-[421/614]">
                    {image ? (
                      <img
                        src={image}
                        alt={card?.name}
                        className="w-full h-full object-cover"
                        style={{ transform: 'scale(1.02)' }}
                        onError={e => { e.target.onerror = null; e.target.style.display = 'none'; }}
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center p-4">
                        <p className="text-gray-400 text-center text-sm">{card?.name}</p>
                      </div>
                    )}
                    {/* Glare overlay */}
                    <div
                      className="absolute inset-0 pointer-events-none rounded-xl"
                      style={{
                        background: `radial-gradient(circle at ${glarePos.x}% ${glarePos.y}%, rgba(255,255,255,0.15) 0%, transparent 60%)`,
                      }}
                    />
                  </div>
                </div>
                {/* "Girar" hint */}
                <p className="text-center text-gray-600 text-xs mt-2">Clic para voltear</p>
              </div>

              {/* Back face: Card Info */}
              <div
                className="absolute inset-0 rounded-xl overflow-hidden flex flex-col justify-between p-4"
                style={{
                  backfaceVisibility: 'hidden',
                  transform: 'rotateY(180deg)',
                  background: 'linear-gradient(135deg,#0d0d1a,#1a0505)',
                  border: '2px solid rgba(212,175,55,0.4)',
                  boxShadow: '0 20px 60px rgba(0,0,0,0.8)',
                }}
              >
                <p className="text-yellow-400 font-black text-sm text-center" style={{ fontFamily: 'Cinzel,serif' }}>{card?.name}</p>
                <div className="flex-1 overflow-y-auto">
                  <p className="text-gray-400 text-xs leading-relaxed mt-2">{card?.desc?.substring(0, 250)}{card?.desc?.length > 250 ? '...' : ''}</p>
                </div>
                {isMonster && (
                  <div className="flex justify-around mt-2 p-2 rounded-lg" style={{ background: 'rgba(212,175,55,0.1)' }}>
                    {level && <div className="text-center"><span className="text-yellow-400 font-black">{level}</span><p className="text-gray-500 text-[10px]">LVL</p></div>}
                    {atk !== undefined && <div className="text-center"><span className="text-red-400 font-black">{atk}</span><p className="text-gray-500 text-[10px]">ATK</p></div>}
                    {def !== undefined && <div className="text-center"><span className="text-blue-400 font-black">{def}</span><p className="text-gray-500 text-[10px]">DEF</p></div>}
                  </div>
                )}
                <p className="text-center text-gray-600 text-xs mt-2">Clic para volver</p>
              </div>
            </div>

            {/* Reflection */}
            <div className="w-full mt-2 h-16 rounded-xl opacity-20 pointer-events-none overflow-hidden" style={{ transform: 'scaleY(-1)', filter: 'blur(4px)' }}>
              {image && <img src={image} alt="" className="w-full h-full object-cover" style={{ objectPosition: 'top' }} />}
            </div>
          </div>

          {/* Right: Card info & stats */}
          <div
            className="flex-1 max-w-xl"
            style={{
              opacity: visible ? 1 : 0,
              transform: visible ? 'translateX(0)' : 'translateX(40px)',
              transition: 'all 0.8s cubic-bezier(0.16,1,0.3,1) 0.2s',
            }}
          >
            {/* Label */}
            <div className="flex items-center gap-3 mb-5">
              <div className="px-4 py-1.5 rounded-full text-xs font-black uppercase tracking-widest" style={{ background: 'rgba(212,175,55,0.15)', border: '1px solid rgba(212,175,55,0.4)', color: '#d4af37' }}>
                Carta del Día
              </div>
              <span className="text-gray-500 text-xs capitalize">{today}</span>
            </div>

            <h2 className="font-black mb-2 leading-tight" style={{
              fontFamily: 'Cinzel,serif',
              fontSize: 'clamp(1.8rem,4vw,3rem)',
              background: 'linear-gradient(135deg,#fff,#d4af37)',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
            }}>
              {loading ? '...' : card?.name || 'Sin carta'}
            </h2>

            <p className="text-gray-400 text-sm mb-1 capitalize">{card?.type}</p>
            {card?.race && <p className="text-gray-500 text-xs mb-4">{card.race}</p>}

            {/* Stats */}
            {isMonster && (atk !== undefined || def !== undefined || level) && (
              <div className="flex flex-wrap gap-4 mb-6 p-4 rounded-2xl" style={{ background: 'rgba(212,175,55,0.06)', border: '1px solid rgba(212,175,55,0.15)' }}>
                {level && (
                  <div className="text-center">
                    <div className="text-2xl font-black text-yellow-400">{level}</div>
                    <div className="text-gray-500 text-xs uppercase">Nivel</div>
                  </div>
                )}
                {atk !== undefined && (
                  <div className="text-center">
                    <div className="text-2xl font-black text-red-400">{atk === -1 ? '?' : atk}</div>
                    <div className="text-gray-500 text-xs uppercase">ATK</div>
                  </div>
                )}
                {def !== undefined && (
                  <div className="text-center">
                    <div className="text-2xl font-black text-blue-400">{def === -1 ? '?' : def}</div>
                    <div className="text-gray-500 text-xs uppercase">DEF</div>
                  </div>
                )}
                {card?.attribute && (
                  <div className="text-center">
                    <div className="text-base font-black text-orange-400">{card.attribute}</div>
                    <div className="text-gray-500 text-xs uppercase">Atrib.</div>
                  </div>
                )}
              </div>
            )}

            {/* Description */}
            {card?.desc && (
              <p className="text-gray-400 text-sm leading-relaxed mb-6 line-clamp-4">{card.desc}</p>
            )}

            {/* Share */}
            <div className="flex flex-wrap gap-3">
              <a
                href={`https://wa.me/?text=${encodeURIComponent(shareText)}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold text-sm transition-all hover:scale-105"
                style={{ background: '#25D366', color: '#fff', boxShadow: '0 4px 15px rgba(37,211,102,0.3)' }}
                data-testid="share-whatsapp"
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
                WhatsApp
              </a>
              <a
                href={`https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}`}
                target="_blank" rel="noreferrer"
                className="inline-flex items-center gap-2 px-5 py-3 rounded-full font-bold text-sm transition-all hover:scale-105"
                style={{ background: '#1DA1F2', color: '#fff', boxShadow: '0 4px 15px rgba(29,161,242,0.3)' }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M23.953 4.57a10 10 0 01-2.825.775 4.958 4.958 0 002.163-2.723c-.951.555-2.005.959-3.127 1.184a4.92 4.92 0 00-8.384 4.482C7.69 8.095 4.067 6.13 1.64 3.162a4.822 4.822 0 00-.666 2.475c0 1.71.87 3.213 2.188 4.096a4.904 4.904 0 01-2.228-.616v.06a4.923 4.923 0 003.946 4.827 4.996 4.996 0 01-2.212.085 4.936 4.936 0 004.604 3.417 9.867 9.867 0 01-6.102 2.105c-.39 0-.779-.023-1.17-.067a13.995 13.995 0 007.557 2.209c9.053 0 13.998-7.496 13.998-13.985 0-.21 0-.42-.015-.63A9.935 9.935 0 0024 4.59z"/></svg>
                Twitter
              </a>
            </div>
          </div>
        </div>
      </div>

      {/* Spinning conic gradient animation */}
      <style>{`@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }`}</style>
    </section>
  );
};

export default CardOfTheDay;
